import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import classnames from "classnames";
import { useDebounce } from "../../hooks/index.js";
import { Icon } from "../../Icon/index.js";
import { useAutocompleteReducer } from "../hooks/index.js";
import { Label, Preloader } from "../common/index.js";
import { Box } from "../../Box/index.js";
import { TextFieldMasked } from "../TextFieldMasked/index.js";
import { ListBox } from "./ListBox.js";
import AddressInput_styles from "./AddressInput.styles.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function AddressInput_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else AddressInput_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
const DEBOUNCE_TIMEOUT = 200;
const SHOW_RESULT_LIMIT = 10;
const AddressInput = (props)=>{
    var _useDebounce;
    const { id, name, label, labelIcon, value, onChange, onBlur, placeholder, disabled = false, required = false, loading = false, tooltip, error = false, helperText, noOptionsText, renderOption, limit = 50, minInputValueLength = 1, mask, testId } = props;
    const { items, status, hasMore, requestItems, resetItems } = useAutocompleteReducer(props.loadItems, true);
    const { classes } = AddressInput_styles();
    const reactId = useId();
    const inputId = useMemo(()=>null != id ? id : reactId, [
        id,
        reactId
    ]);
    const options = items.map((option)=>option.value);
    const [isLoading, setLoading] = useState(false);
    const [filter, setFilter] = useState({
        limit,
        offset: 0,
        query: ''
    });
    const [open, setOpen] = useState(false);
    const [lastSelectedValue, setLastSelectedValue] = useState(void 0);
    const debouncedInputValue = null != (_useDebounce = useDebounce(value, DEBOUNCE_TIMEOUT)) ? _useDebounce : '';
    const isItemsLoading = isLoading || loading || 'loading' === status || open && void 0 !== value && value.replace(/_+/g, '').trim().length < minInputValueLength;
    const handleChange = useCallback((event, nextValue, reason)=>{
        if ('selectOption' === reason) setLastSelectedValue(null != nextValue ? nextValue : void 0);
        onChange(null != nextValue ? nextValue : void 0);
        resetItems();
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: 0,
            query: ''
        }));
        setOpen(false);
    }, [
        onChange,
        filter,
        resetItems
    ]);
    const handleInputChange = useCallback((event, nextValue, reason)=>{
        if ('reset' === reason) return;
        if ('clear' === reason) setLastSelectedValue(void 0);
        onChange(null != nextValue ? nextValue : void 0);
        if (null == nextValue ? void 0 : nextValue.replace(/_+/g, '').trim()) {
            if (!open) setOpen(true);
        } else setOpen(false);
    }, [
        onChange,
        open
    ]);
    const handleClose = useCallback(()=>{
        null == onBlur || onBlur();
        if (open) setOpen(false);
    }, [
        onBlur,
        open
    ]);
    const handleBlur = useCallback(()=>{
        onChange(lastSelectedValue);
    }, [
        onChange,
        lastSelectedValue
    ]);
    const handleOpen = useCallback(()=>{
        if (null == value ? void 0 : value.replace(/_+/g, '').trim()) setOpen(true);
    }, [
        value
    ]);
    const requestNextItems = ()=>{
        null == requestItems || requestItems(filter, 'get');
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: filter.offset + filter.limit
        }));
    };
    useEffect(()=>{
        if (!debouncedInputValue || debouncedInputValue.length < minInputValueLength) return;
        const abortRequest = null == requestItems ? void 0 : requestItems(_object_spread_props(_object_spread({}, filter), {
            offset: 0,
            query: debouncedInputValue.replace(/_+/g, '').trim()
        }), 'search');
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: filter.limit
        }));
        setLoading(false);
        return ()=>{
            null == abortRequest || abortRequest();
        };
    }, [
        open,
        debouncedInputValue
    ]);
    useEffect(()=>{
        if (!open && value) handleChange({}, value, 'select-option');
    }, []);
    const filterOptions = (optionList)=>optionList.slice(0, SHOW_RESULT_LIMIT);
    return /*#__PURE__*/ jsxs(FormControl, {
        className: classnames(classes.addressInput, 'aqa_address_input'),
        children: [
            label && /*#__PURE__*/ jsx(Label, {
                label: label,
                required: required,
                tooltip: tooltip,
                icon: labelIcon,
                inputId: inputId
            }),
            /*#__PURE__*/ jsx(Autocomplete, {
                "data-testid": testId,
                classes: _object_spread({
                    paper: classes.paper,
                    option: classes.option
                }, value ? {} : {
                    clearIndicator: classes.clearIndicatorHide
                }),
                options: isItemsLoading ? [] : options,
                filterOptions: filterOptions,
                fullWidth: true,
                noOptionsText: noOptionsText,
                renderTags: ()=>null,
                disabled: disabled || loading,
                clearIcon: /*#__PURE__*/ jsx(Icon, {
                    name: "dismiss_substract",
                    color: "stroke.secondary",
                    size: "24"
                }),
                loading: isItemsLoading,
                loadingText: /*#__PURE__*/ jsx(Box, {
                    display: "flex",
                    justifyContent: "center",
                    p: "16",
                    children: /*#__PURE__*/ jsx(CircularProgress, {
                        size: 24
                    })
                }),
                inputValue: loading ? 'Загрузка...' : value,
                onInputChange: mask ? void 0 : handleInputChange,
                onChange: handleChange,
                open: open,
                onOpen: handleOpen,
                onClose: handleClose,
                onBlur: handleBlur,
                renderOption: (renderOptionProps, option)=>/*#__PURE__*/ jsx("li", _object_spread_props(_object_spread({}, renderOptionProps), {
                        children: renderOption ? renderOption(option) : /*#__PURE__*/ jsx("div", {
                            children: option
                        })
                    })),
                slotProps: {
                    listbox: _object_spread({
                        component: ListBox
                    }, {
                        dataLength: options.length,
                        next: requestNextItems,
                        hasMore,
                        loading: isItemsLoading,
                        long: !!renderOption
                    })
                },
                renderInput: (params)=>mask ? /*#__PURE__*/ jsx(TextFieldMasked, _object_spread_props(_object_spread({}, params), {
                        id: inputId,
                        mask: mask,
                        name: name,
                        error: error,
                        helperText: helperText,
                        placeholder: placeholder || label,
                        loading: loading,
                        classes: {
                            root: classes.inputRoot
                        },
                        onChange: handleInputChange
                    })) : /*#__PURE__*/ jsx(TextField, _object_spread(_object_spread_props(_object_spread({}, params), {
                        id: inputId,
                        name: name,
                        classes: {
                            root: classes.inputRoot
                        },
                        autoComplete: "off",
                        variant: "outlined",
                        fullWidth: true,
                        error: error,
                        helperText: helperText,
                        placeholder: placeholder || label
                    }), loading ? {
                        slotProps: {
                            input: {
                                endAdornment: /*#__PURE__*/ jsx(Preloader, {})
                            }
                        }
                    } : {}))
            })
        ]
    });
};
export { AddressInput };
